import React, { useEffect, useMemo, useState } from "react";
import { View } from "react-native";
import _ from 'lodash';
import HelperSetting from "utils/helper-setting";
import HelperValidate from "utils/helper-validate";
import i18n from "utils/i18n";
import Calendar from 'components/organisms/Calendar';
import { dataConstants } from "services/redux/data/constants";
import { useSelector } from "react-redux";
import FetchBooking from "utils/fetch/FetchBooking";
import FetchTimeslotCapacity from "utils/fetch/FetchTimeslotCapacity";
import { settingsConstants } from "utils/constants/settings-constants";
import { PlaceholderCalendarStrip } from "placeholders";
import ObjectBooking from "utils/objects/booking";
import Timeslot from "components/molecules/Timeslot";
import ThemedBlock from "components/atoms/ThemedBlock";
import LoadingComponent from "components/atoms/Loading";
import NoResultsView from "components/atoms/NoResultsView";
import ThemedList from 'components/atoms/ThemedList'
import ThemedBlockListContainer from 'components/atoms/ThemedBlockListContainer'
import HelperPlatform from "utils/helper-platform";
import HelperString from 'utils/helper-string'
import TextDivider from "components/atoms/TextDivider";
import WebSplitView from 'components/atoms/WebSplitView';
import selectBookings from 'services/redux/data/selectors/selectBookings'
import { useIsInManagement } from 'utils/helper-linking'
import { useIsSmallDevice } from 'utils/helper-device'
import { useStateWithCallbackLazy } from 'use-state-with-callback'
import { timeslotKindConstants } from 'utils/constants/timeslot-kind-constants'
import isEqual from 'react-fast-compare'
import { themedCalendarStripRenderDayStyles } from 'styles/components/atoms/ThemedCalendarStrip'
import HelperDate, { dateFormat } from 'utils/helper-date'

const ThemedCalendarStrip = ({ date = HelperDate.date(), employee = { name: '' }, onSelect, onModeChange, renderItem, renderEmptyItem, selectDate, timeslotKinds = {}, bookings, withActions = true, showQrCode = false, showEmployee = false, showLunchOptions = true, showBuildingParkingSpace = true, enableMinDate = true, disableTimeslots = false, isLoading = false, filterCalendarDayItems = null, stacked = false, children  }) => {

	const _bookings = useSelector( selectBookings )
	const isInManagement = useIsInManagement()
	const isSmallDevice = useIsSmallDevice()
	
	const [ loading, setLoading ] = useState( true )
	const [ minDate, setMinDate ] = useState( HelperDate.date() )
	const [ maxDate, setMaxDate ] = useState( null )
	const [ selectedDate, setSelectedDate ] = useState( undefined )
	const [ selectedDateLoading, setSelectedDateLoading ] = useState( { [ date.format( dateFormat.DATE ) ] : true } )
	const [ items, setItems ] = useStateWithCallbackLazy( {} )
	const [ markedItems, setMarkedItems ] = useState( {} )
	
	const calendarDate = useMemo( () => {
		const value = ( !selectedDate ? date : selectedDate.format( dateFormat.DATE ))
		
		return HelperDate.date( 'm:'+ value )
	}, [ selectedDate, date ])
	
	const getBookings = () => {
		if( bookings ) {
			return bookings;
		}
		
		return _bookings;
	}
	
	const loadItems = () => {
		setMarkedItems( ObjectBooking.getDots( getBookings(), { oneOfAKind: true } ) )
	}
	
	const createDateString = ( date ) => {
		if( date === undefined ){
			return '';
		}
		
		return date.format( dateFormat.DATE );
	}
	
	const onSelectDate = ( date, forceSelect = false ) => {
		// When the selected date is more then the maximum date -> select the maximum date
		if( maxDate !== null && date.isAfter( maxDate ) ) {
			date = maxDate
		}
		
		// Prevent selecting the same day twice
		if( date.isSame( calendarDate, 'day' ) && !forceSelect ) {
			return;
		}
		
		setSelectedDate( date )
		setSelectedDateLoading({ ...selectedDateLoading, [ date.format( dateFormat.DATE ) ]: true } )
		
		// Check the on select
		if( _.isFunction( onSelect ) ) {
			onSelect( date );
		}
		
		// Set the items
		let updatedItems = {};
		
		// With function
		if( selectDate !== null && _.isFunction( selectDate ) ) {
			return selectDate( date, ({ date, items }) => {

				// if( typeof date === 'string' ) {
				// 	date = HelperDate.date( date );
				// }

				// To be sure: filter by date
				const dateYmd = HelperDate.date( date ).format( dateFormat.DATE )
				
				updatedItems[ dateYmd ] = _.filter( items, { booking_date: dateYmd } );

				setItems( { ...updatedItems }, () => {
					setSelectedDateLoading({ ...selectedDateLoading, [ dateYmd ]: false })
				})
			});
		}
		
		// Default way
		const dateFormatted = date.format( dateFormat.DATE );
		
		const bookings = getBookings();
		const dayBookings = ObjectBooking.filterAccepted(
			_.filter( bookings, {
				'booking_date': dateFormatted
			})
		);
		
		// Today
		_.forEach( dayBookings, booking => {
			const bookingDate = booking.dayjs.format( dateFormat.DATE );
			
			if( !_.has( updatedItems, bookingDate ) ) {
				updatedItems[ bookingDate ] = [];
			}
			
			updatedItems[ bookingDate ].push( booking );
		});
		
		setItems( { ...updatedItems }, () => {
			setSelectedDateLoading({ ...selectedDateLoading, [ dateFormatted ]: false })
		})
	}
	
	useEffect( () => {
		if( isInManagement ) {
			let minDateString = 'year:1';
			
			const { date: newMinDate } = HelperDate.split( minDateString, false );
			
			setMinDate( newMinDate )
		}
	}, [ isInManagement ] )
	
	useEffect( () => {
		// On load select the initial date
		onSelectDate( date, true );

		loadItems();
		
		// Get the min date
		let minDateString = HelperSetting.get( settingsConstants.CALENDAR_MIN_DATE );
		if( !enableMinDate ) {
			minDateString = 'day:0';
		}
		
		const { date: newMinDate } = HelperDate.split( minDateString, false );
		
		// Get the max date
		const { date: newMaxDate } = HelperDate.split( HelperSetting.get( settingsConstants.CALENDAR_MAX_DATE ) );
		
		setLoading( false )
		setMinDate( newMinDate )
		setMaxDate( newMaxDate )
	}, [] )
	
	useEffect( () => {
		if( !disableTimeslots ) {
			FetchBooking.resetMonthsFetched()
			loadItems()
		}
		
		if( !loading ) {
			onSelectDate( calendarDate, true )
		}
	}, [ bookings, _bookings ])
	
	useEffect( () => {
		if( !date.isSame( selectedDate, 'day' ) ) {
			onSelectDate( date );
		}
	}, [ date ] );
	
	if( loading ) {
		return (
			<PlaceholderCalendarStrip key={"calendar-strip-placeholder"} />
		)
	}
	
	const newCalendar = (
		<ThemedBlock
			key={ HelperString.hash( "calendar_block" ) }
			shadow
			smallPadding>
			
			<Calendar
				key={ HelperString.hash( "calendar" ) }
				calendarDate={ calendarDate }
				dateDots={ markedItems }
				minDate={ createDateString( minDate ) }
				maxDate={ createDateString( maxDate ) }
				onModeChange={ onModeChange }
				onDayPress={ onSelectDate }
			/>
		</ThemedBlock>
	);

	let dayItems = <LoadingComponent size={ 'm' } />;
	if( !isLoading ) {
		dayItems = null
		
		if( !disableTimeslots ) {
			// Get the items for the selected day
			let calendarDayItems = _.get( items, calendarDate.format( dateFormat.DATE ), [] );
			
			if( _.isFunction( filterCalendarDayItems ) ){
				calendarDayItems = filterCalendarDayItems( calendarDayItems );
			}
			
			switch( true ) {
				case _.get( selectedDateLoading, calendarDate.format( dateFormat.DATE ), true ):
					dayItems = (
						<LoadingComponent
							key={ HelperString.hash( "loading_component", date.format( dateFormat.DATE_TIME ) ) }
						/>
					)
					break;
					
				case _.size( calendarDayItems ) > 0:
					const calenderListContainers = [];
					
					let items = [];
					if( _.size( timeslotKinds ) > 0  ){
						_.forEach( _.size( timeslotKinds ) > 0 ? timeslotKinds : timeslotKindConstants, kind => {
							items = [...items, ..._.filter( calendarDayItems, { kind: kind } ) ];
						});
					}else{
						items = _.sortBy( calendarDayItems, (item ) => {
							return item.timeslot.timeslot_start;
						});
					}
					
					calenderListContainers.push(
						<ThemedBlockListContainer
							key={ HelperString.hash( "calenderList_container", date.format( dateFormat.DATE_TIME ) ) }>
							
							<ThemedList
								key={ HelperString.hash( "calenderList", date.format( dateFormat.DATE_TIME ) ) }
								data={ items }
								dividerEnabled={ true }
								keyExtractor={ ( item, index ) => {
									return HelperString.hash(`calendar-strip-day-item`, FetchTimeslotCapacity.keyByBooking(item));
								} }
								scrollEnabled={ false }
								maxRenderSize={ 8 }
								renderItem={ ( props ) => {
									return (
										<RenderDay
											{ ...props }
											key={ HelperString.hash( `calendar-strip-day-item`, FetchTimeslotCapacity.keyByBooking( props.item ) ) }
											date={ calendarDate }
											withActions={ withActions }
											renderItem={ renderItem }
											employee={ employee }
											showQrcode={ showQrCode }
											showEmployee={ showEmployee }
											showLunchOptions={ showLunchOptions }
											showBuildingParkingSpace={ showBuildingParkingSpace }
										/>
									);
								}}
							/>
						</ThemedBlockListContainer>
					);
					
					dayItems = (
						<ThemedBlock
							key={ HelperString.hash( "calendar_list_container_block") }
							shadow>
							
							{ calenderListContainers }
						</ThemedBlock>
					)
					break;
					
				default:
					dayItems = (
						<RenderEmptyItem
							renderItem={ renderEmptyItem }
							employee={ employee }
							date={ calendarDate }
						/>
					)
			}
		}
	}
	
	const selectedDateDivider = _.capitalize( selectedDate.format( 'dddd D MMMM' ) );
	
	const dateDivider = (
		<TextDivider
			key={ HelperString.hash( "calendar-date-divider", date.format( dateFormat.DATE_TIME ) ) }
			text={ selectedDateDivider}
		/>
	)
	
	if( !stacked && HelperPlatform.isWeb() && !isSmallDevice ) {
		return (
			<WebSplitView
                key={ HelperString.hash( "web_split_view", date.format( dateFormat.DATE_TIME ) ) }
                leftItems={ newCalendar }
                rightItems={ [ dateDivider, dayItems, children ]  }
			/>
		);
	}
	
	return (
		<View
			key={ HelperString.hash( "themed-calender-strip-view" ) }>
			
			{ newCalendar }
			{ dateDivider }
			{ dayItems }
			{ children }
		</View>
	)
}

const RenderEmptyItem = ({ item, date, renderItem, employee }) => {
	if( renderItem ) {
		return renderItem( item, {
			callback: ( result ) => {
			}
		});
	}
	
	const _employee = useSelector( state => {
		return state.data[dataConstants.EMPLOYEE];
	} );
	
	const dateLabel = date.format( 'D MMMM' );
	const employeeLabel = employee?.name;
	
	let i18nKey = 'page.booking.no_booking_found_date';
	if( _employee.guid !== employee?.guid ){
		i18nKey = 'page.booking.no_booking_found_date_coworker';
	}
	
	return (
		<View
			key={ HelperString.hash( 'render-empty-item', FetchTimeslotCapacity.keyByBooking( item ) ) }>
			
			<NoResultsView>
				{ i18n.t(i18nKey, { date : dateLabel, name: employeeLabel  } ) }
			</NoResultsView>
		</View>
	)
}

const RenderDay = ({ item, date, employee, renderItem, withActions, showQrcode, showEmployee, showLunchOptions, showBuildingParkingSpace }) => {
	
	// When item is NOT valid render the empty item
	if( !HelperValidate.isValid( item ) ) {
		return (
			<RenderEmptyItem
				employee={ employee }
				item={ item }
				date={ date }
			/>
		)
	}
	
	let content;
	if( renderItem ) {
		content = renderItem( item, {
			callback: ( result ) => {
			}
		})
	} else {
		content = (
			<Timeslot
				key={ HelperString.hash( 'calendar-list-item', FetchTimeslotCapacity.keyByBooking( item ) ) }
				item={ item }
				itemProps={{
					showQrCode: showQrcode,
					showEmployee: showEmployee,
					showLunchOptions: showLunchOptions,
					showBuildingParkingSpace: showBuildingParkingSpace
				}}
				withActions={ withActions }
			/>
		)
	}
	
	return (
		<View
			key={ HelperString.hash( 'calendar-list-item-view', FetchTimeslotCapacity.keyByBooking( item ) ) }
			style={ themedCalendarStripRenderDayStyles.view }>
			
			{ content }
		</View>
	);
}

export default React.memo( ThemedCalendarStrip, isEqual );
